import React, { useState, useEffect, useCallback } from 'react';
import { X, Gift, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ExitIntentPopup = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  // Lazy init from sessionStorage so we don't have to setState inside an effect
  const [hasShown, setHasShown] = useState(() => {
    if (typeof window === 'undefined') return false;
    return sessionStorage.getItem('exitPopupShown') === 'true';
  });

  const handleMouseLeave = useCallback((e) => {
    if (e.clientY <= 0 && !hasShown && !isVisible) {
      setIsVisible(true);
      setHasShown(true);
      sessionStorage.setItem('exitPopupShown', 'true');
    }
  }, [hasShown, isVisible]);

  useEffect(() => {
    if (hasShown) return undefined;

    const timer = setTimeout(() => {
      document.addEventListener('mouseleave', handleMouseLeave);
    }, 5000);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [hasShown, handleMouseLeave]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !email.includes('@')) {
      alert('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);

    try {
      await axios.post(`${API}/email-capture`, {
        email: email,
        source: 'exit_intent',
        discount_code: 'GODDESS10'
      });

      setIsSuccess(true);
      
      setTimeout(() => {
        setIsVisible(false);
      }, 5000);
    } catch (error) {
      alert('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm animate-in fade-in duration-300">
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden animate-in zoom-in duration-300">
        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors z-10"
        >
          <X className="w-6 h-6" />
        </button>

        {/* Content */}
        {!isSuccess ? (
          <div className="p-8">
            {/* Icon */}
            <div className="w-16 h-16 bg-gradient-to-br from-[#7d1f3d] to-[#9d2f4d] rounded-full flex items-center justify-center mx-auto mb-6">
              <Gift className="w-8 h-8 text-white" />
            </div>

            {/* Heading */}
            <h2 className="text-3xl font-bold text-[#7d1f3d] text-center mb-3">
              Wait! Don&apos;t Miss Out
            </h2>
            
            <p className="text-lg text-gray-700 text-center mb-6">
              Get <span className="font-bold text-[#d4af37]">10% OFF</span> your next order!
            </p>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <Input
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 py-6 text-lg border-2 border-gray-200 focus:border-[#7d1f3d] rounded-xl"
                  disabled={isSubmitting}
                />
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-[#7d1f3d] to-[#9d2f4d] hover:from-[#5d1729] hover:to-[#7d1f3d] text-white text-lg py-6 rounded-xl shadow-lg font-bold disabled:opacity-50"
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Claiming...
                  </>
                ) : (
                  'Claim My 10% Discount'
                )}
              </Button>
            </form>

            <p className="text-xs text-gray-500 text-center mt-4">
              Plus exclusive updates on new arrivals and special offers
            </p>
          </div>
        ) : (
          <div className="p-8 text-center">
            {/* Success Icon */}
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Gift className="w-8 h-8 text-green-600" />
            </div>

            {/* Success Message */}
            <h2 className="text-3xl font-bold text-[#7d1f3d] mb-4">
              🎉 Success!
            </h2>
            
            <p className="text-lg text-gray-700 mb-6">
              Check your email for your exclusive discount code:
            </p>

            <div className="bg-gradient-to-r from-[#7d1f3d] to-[#9d2f4d] text-white px-6 py-4 rounded-xl mb-6">
              <p className="text-sm font-medium mb-1">Your Discount Code</p>
              <p className="text-3xl font-bold tracking-wider">GODDESS10</p>
              <p className="text-sm mt-2 opacity-90">10% off your next purchase</p>
            </div>

            <p className="text-sm text-gray-600">
              Save this code for your next visit!
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
